import React from 'react';

import axios from '../../utils/axiosConfig';
import Content from '../../components/Content';
import Loader from '../../components/Loader';
import Table from '../../components/Table';

import styles from './Blocking.module.css';

// WatcherBlock — the control surface for active blocking. The per-appliance
// switch drives ApplianceSettings.blocking_enabled; the rule tables drive the
// global ip/country/port policy with action='block', which the appliance's
// watcher-block service enforces with tcpkill. Deny rules (alert-only) live on
// the Denied Lists page instead.

const BlockingScreen = () => {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const [ipInput, setIpInput] = React.useState('');
  const [countryInput, setCountryInput] = React.useState('');
  const [portInput, setPortInput] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get('/api/blocking');
      if (res && res.status) setData(res);
    } catch (e) { /* leave prior state */ }
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const post = async (body) => {
    setMsg('');
    try {
      const { data: res } = await axios.post('/api/blocking', body,
        { headers: { 'Content-Type': 'application/json' } });
      if (res && res.status) {
        setData(res);
      } else if (res && res.msg) {
        setMsg(res.msg);
      }
    } catch (e) { setMsg('Request failed'); }
  };

  const toggle = (appliance) =>
    post({ btn: 'ToggleBlocking', applianceId: appliance.id, enabled: !appliance.blockingEnabled });

  const addRule = (btn, value, clear) => {
    const v = String(value || '').trim();
    if (!v) return;
    post({ btn, value: v }).then(() => clear(''));
  };

  const summary = data?.summary || {};
  const appliances = data?.appliances || [];

  const ruleTable = (title, cols, rows, keyCol, delBtn, input, setInput, addBtn, placeholder) => (
    <div className={styles.rule_block}>
      <div className={styles.rule_head}>
        <h3 className={styles.rule_title}>{title}</h3>
        <div className={styles.add_row}>
          <input
            className={styles.add_input}
            placeholder={placeholder}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addRule(addBtn, input, setInput); }}
          />
          <button className={styles.add_btn} onClick={() => addRule(addBtn, input, setInput)}>Block</button>
        </div>
      </div>
      <Table
        tableHead={<tr>{cols.map((c) => <th key={c}>{c}</th>)}<th>Action</th></tr>}
        tableRow={
          rows.length > 0 ? (
            rows.map((r, i) => (
              <tr key={i}>
                {r.map((cell, j) => <td key={j} className={j === 0 ? styles.mono : undefined}>{cell}</td>)}
                <td>
                  <button
                    className={styles.del_btn}
                    onClick={() => post({ btn: delBtn, value: String(r[keyCol]) })}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr><td colSpan={cols.length + 1} style={{ textAlign: 'center' }}>No block rules yet.</td></tr>
          )
        }
      />
    </div>
  );

  return (
    <Content currentMenu='configuration' headerTitle='WatcherBlock'>
      <div className={styles.intro}>
        Active blocking tears down connections that match a block rule by injecting
        TCP resets (tcpkill) on the appliance. It is <strong>off by default</strong> and
        enabled per watcher below. Block rules are global — they apply to every watcher
        that has blocking enabled. Rules that should only <em>alert</em> belong on
        Denied Lists; these ones enforce.
      </div>

      {msg && <div className={styles.msg}>{msg}</div>}

      <div className={styles.summary_grid}>
        <div className={styles.summary_card}>
          <span className={styles.summary_value}>{summary.enabled || 0}/{summary.appliances || 0}</span>
          <span className={styles.summary_label}>Watchers Enforcing</span>
        </div>
        <div className={styles.summary_card}>
          <span className={styles.summary_value}>{summary.ipRules || 0}</span>
          <span className={styles.summary_label}>IP Blocks</span>
        </div>
        <div className={styles.summary_card}>
          <span className={styles.summary_value}>{summary.countryRules || 0}</span>
          <span className={styles.summary_label}>Country Blocks</span>
        </div>
        <div className={styles.summary_card}>
          <span className={styles.summary_value}>{summary.portRules || 0}</span>
          <span className={styles.summary_label}>Port Blocks</span>
        </div>
      </div>

      {loading && !data ? (
        <Loader />
      ) : (
        <>
          <h3 className={styles.section_title}>Watchers</h3>
          <div className={styles.table_wrap}>
            <Table
              tableHead={<tr><th>Watcher</th><th>Blocking</th><th>State</th><th>Last Seen</th><th>Action</th></tr>}
              tableRow={
                appliances.length > 0 ? (
                  appliances.map((a) => (
                    <tr key={a.id} className={a.blockingLicensed && a.blockingEnabled ? styles.on_row : undefined}>
                      <td>{a.watcher}</td>
                      <td>
                        {a.blockingLicensed ? (
                          <span className={a.blockingEnabled ? styles.badge_on : styles.badge_off}>
                            {a.blockingEnabled ? 'ON' : 'OFF'}
                          </span>
                        ) : (
                          <span className={styles.badge_off}>—</span>
                        )}
                      </td>
                      <td>{a.state}</td>
                      <td className={styles.mono}>{a.lastSeen || '—'}</td>
                      <td>
                        {a.blockingLicensed ? (
                          <button
                            className={a.blockingEnabled ? styles.disable_btn : styles.enable_btn}
                            onClick={() => toggle(a)}
                          >
                            {a.blockingEnabled ? 'Disable' : 'Enable'}
                          </button>
                        ) : (
                          <span className={styles.badge_off} title="This Watcher is not licensed for Blocking — enable it on the Watchers page.">
                            Not licensed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={5} style={{ textAlign: 'center' }}>No watchers registered.</td></tr>
                )
              }
            />
          </div>

          <h3 className={styles.section_title}>Block Rules</h3>
          {ruleTable('IP / Network', data?.IPCols || [], data?.IPRows || [], 0,
            'DeleteBlockIP', ipInput, setIpInput, 'AddBlockIP', 'IP or CIDR, e.g. 203.0.113.9')}
          {ruleTable('Country', data?.CountryCols || [], data?.CountryRows || [], 0,
            'DeleteBlockCountry', countryInput, setCountryInput, 'AddBlockCountry', 'ISO code, e.g. RU')}
          {ruleTable('Port', data?.PortCols || [], data?.PortRows || [], 0,
            'DeleteBlockPort', portInput, setPortInput, 'AddBlockPort', 'Port, e.g. 4444')}
        </>
      )}
    </Content>
  );
};

export default BlockingScreen;
